import React, { useState, useEffect, useCallback } from 'react';
import { 
  Package, 
  AlertTriangle, 
  XCircle, 
  Plus, 
  Minus, 
  RefreshCcw,
  History,
  Search,
  X,
  Check,
  ArrowUpCircle,
  ArrowDownCircle,
  Clock
} from 'lucide-react';
import { useProducts } from '../../context/ProductContext';
import inventoryService, { InventoryReason } from '../../services/inventoryService';
import { fuzzyMatch } from '../../lib/fuzzySearch';

const InventoryManagement = () => {
  const { products, refreshProducts } = useProducts();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch inventory logs
  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const data = await inventoryService.getLogs();
      setLogs(data);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchLogs();
    }
  }, [activeTab, fetchLogs]);

  // Get stock statistics
  const lowStockProducts = inventoryService.getLowStockProducts(products, 10);
  const outOfStockProducts = inventoryService.getOutOfStockProducts(products);
  const visibleProducts = products.filter(p => p.isAvailable);
  const healthyStockProducts = visibleProducts.filter(p => p.stockQuantity > 10);

  // Filtered products for overview
  const filteredProducts = visibleProducts.filter(p =>
    fuzzyMatch(searchTerm, p.name)
  );

  // Handle restock
  const handleRestock = async (productId, quantity, note) => {
    setLoading(true);
    setError(null);
    try {
      await inventoryService.restock(productId, quantity, note);
      await refreshProducts();
      setSuccess(`Successfully restocked ${quantity} units`);
      setShowRestockModal(false);
      setSelectedProduct(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to restock');
    } finally {
      setLoading(false);
    }
  };

  // Handle adjustment
  const handleAdjust = async (productId, quantityChange, reason, note) => {
    setLoading(true);
    setError(null);
    try {
      await inventoryService.adjust(productId, quantityChange, reason, note);
      await refreshProducts();
      setSuccess(`Stock adjusted by ${quantityChange > 0 ? '+' : ''}${quantityChange} units`);
      setShowAdjustModal(false);
      setSelectedProduct(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to adjust stock');
    } finally {
      setLoading(false);
    }
  };

  // Open restock modal
  const openRestock = (product) => {
    setSelectedProduct(product);
    setShowRestockModal(true);
    setError(null);
  };

  // Open adjust modal
  const openAdjust = (product) => {
    setSelectedProduct(product);
    setShowAdjustModal(true);
    setError(null);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Inventory Management</h1>
          <p className="text-gray-600 text-sm sm:text-base">Monitor and manage your product stock levels</p>
        </div>
        <button
          onClick={() => { refreshProducts(); if (activeTab === 'history') fetchLogs(); }}
          className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors w-full sm:w-auto"
        >
          <RefreshCcw className="w-5 h-5" />
          Refresh
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 p-4 bg-green-100 border border-green-300 text-green-700 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5" />
          {success}
        </div>
      )}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg flex items-center gap-2">
          <XCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-green-500">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Healthy Stock</p>
              <p className="text-2xl font-bold text-gray-800">{healthyStockProducts.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-orange-500">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Low Stock</p>
              <p className="text-2xl font-bold text-gray-800">{lowStockProducts.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-red-500">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Out of Stock</p>
              <p className="text-2xl font-bold text-gray-800">{outOfStockProducts.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm mb-6">
        <div className="border-b border-gray-100">
          <div className="flex">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 sm:px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'overview'
                  ? 'text-[#548B2E] border-b-2 border-[#548B2E]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Stock Overview
            </button>
            <button
              onClick={() => setActiveTab('alerts')}
              className={`px-4 sm:px-6 py-4 font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
                activeTab === 'alerts'
                  ? 'text-[#548B2E] border-b-2 border-[#548B2E]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Alerts
              {(lowStockProducts.length + outOfStockProducts.length) > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {lowStockProducts.length + outOfStockProducts.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 sm:px-6 py-4 font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
                activeTab === 'history'
                  ? 'text-[#548B2E] border-b-2 border-[#548B2E]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <History className="w-4 h-4" />
              History
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div>
              {/* Search */}
              <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full md:w-80 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
                />
              </div>

              {/* Products Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 lg:px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Product</th>
                      <th className="px-3 lg:px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Unit Type</th>
                      <th className="px-3 lg:px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Current Stock</th>
                      <th className="px-3 lg:px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="px-3 lg:px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredProducts.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#f2f8eb] rounded-lg flex items-center justify-center">
                              <Package className="w-5 h-5 text-[#548B2E]" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{product.name}</p>
                              <p className="text-xs text-gray-500">{product.categoryName || product.weight}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {product.weight ? (
                            <span className="text-sm text-gray-700">{product.weight}</span>
                          ) : (
                            <span className="text-sm text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-lg font-bold text-gray-800">
                            {product.stockQuantity ?? 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {product.stockQuantity === 0 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                              <XCircle className="w-3 h-3" />
                              Out of Stock
                            </span>
                          ) : product.stockQuantity <= 10 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                              <AlertTriangle className="w-3 h-3" />
                              Low Stock
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                              <Check className="w-3 h-3" />
                              In Stock
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openRestock(product)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg text-sm font-medium transition-colors"
                            >
                              <Plus className="w-4 h-4" />
                              Restock
                            </button>
                            <button
                              onClick={() => openAdjust(product)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                            >
                              <Minus className="w-4 h-4" />
                              Adjust
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Alerts Tab */}
          {activeTab === 'alerts' && (
            <div className="space-y-6">
              {/* Out of Stock Section */}
              {outOfStockProducts.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-red-700 mb-3 flex items-center gap-2">
                    <XCircle className="w-5 h-5" />
                    Out of Stock ({outOfStockProducts.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {outOfStockProducts.map((product) => (
                      <div key={product.id} className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-800">{product.name}</h4>
                          <span className="text-xs bg-red-200 text-red-800 px-2 py-1 rounded-full">
                            0 units
                          </span>
                        </div>
                        <button
                          onClick={() => openRestock(product)}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg text-sm font-medium transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Restock Now
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Low Stock Section */}
              {lowStockProducts.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-orange-700 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Low Stock ({lowStockProducts.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {lowStockProducts.map((product) => (
                      <div key={product.id} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-800">{product.name}</h4>
                          <span className="text-xs bg-orange-200 text-orange-800 px-2 py-1 rounded-full">
                            {product.stockQuantity} units
                          </span>
                        </div>
                        <button
                          onClick={() => openRestock(product)}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-orange-600 text-white hover:bg-orange-700 rounded-lg text-sm font-medium transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Restock
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Alerts */}
              {outOfStockProducts.length === 0 && lowStockProducts.length === 0 && (
                <div className="text-center py-12">
                  <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">All Good!</h3>
                  <p className="text-gray-500">All products have healthy stock levels.</p>
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div>
              {logsLoading ? (
                <div className="text-center py-8">
                  <RefreshCcw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-2" />
                  <p className="text-gray-500">Loading history...</p>
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No inventory changes recorded yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {logs.slice(0, 50).map((log) => (
                    <div key={log.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        log.quantityChange > 0 ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {log.quantityChange > 0 ? (
                          <ArrowUpCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <ArrowDownCircle className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">
                          {log.product?.name || `Product #${log.productId}`}
                        </p>
                        <p className="text-sm text-gray-500 flex items-center gap-2">
                          <span className="capitalize">{log.reason}</span>
                          {log.note && <span>• {log.note}</span>}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${log.quantityChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {log.quantityChange > 0 ? '+' : ''}{log.quantityChange}
                        </p>
                        <p className="text-xs text-gray-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(log.createdAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Restock Modal */}
      {showRestockModal && selectedProduct && (
        <RestockModal
          product={selectedProduct}
          onClose={() => { setShowRestockModal(false); setSelectedProduct(null); }}
          onSubmit={handleRestock}
          loading={loading}
        />
      )}

      {/* Adjust Modal */}
      {showAdjustModal && selectedProduct && (
        <AdjustModal
          product={selectedProduct}
          onClose={() => { setShowAdjustModal(false); setSelectedProduct(null); }}
          onSubmit={handleAdjust}
          loading={loading}
        />
      )}
    </div>
  );
};

// Restock Modal Component
const RestockModal = ({ product, onClose, onSubmit, loading }) => {
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!quantity || parseInt(quantity) <= 0) return;
    onSubmit(product.id, parseInt(quantity), note);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800">Restock Product</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Product</p>
            <p className="font-medium text-gray-800">{product.name}</p>
            <p className="text-sm text-gray-500 mt-1">
              Unit Type: <span className="font-medium">{product.weight || 'N/A'}</span>
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Current stock: <span className="font-medium">{product.stockQuantity ?? 0}</span>
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantity to Add *
            </label>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Enter quantity"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
              required
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note (optional)
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., New shipment arrived"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
            />
          </div>

          {quantity && parseInt(quantity) > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-700">
                New stock level will be: <span className="font-bold">{(product.stockQuantity ?? 0) + parseInt(quantity)}</span>
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-[#548B2E] text-white rounded-lg hover:bg-[#3f6b20] transition-colors disabled:opacity-50"
              disabled={loading || !quantity || parseInt(quantity) <= 0}
            >
              {loading ? 'Processing...' : 'Restock'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Adjust Modal Component
const AdjustModal = ({ product, onClose, onSubmit, loading }) => {
  const [quantity, setQuantity] = useState('');
  const [isAddition, setIsAddition] = useState(false);
  const [reason, setReason] = useState(InventoryReason.ADJUSTMENT);
  const [note, setNote] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!quantity || parseInt(quantity) === 0) return;
    const change = isAddition ? parseInt(quantity) : -parseInt(quantity);
    onSubmit(product.id, change, reason, note);
  };

  const newQuantity = (product.stockQuantity ?? 0) + (isAddition ? parseInt(quantity || 0) : -parseInt(quantity || 0));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800">Adjust Stock</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Product</p>
            <p className="font-medium text-gray-800">{product.name}</p>
            <p className="text-sm text-gray-500 mt-1">
              Unit Type: <span className="font-medium">{product.weight || 'N/A'}</span>
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Current stock: <span className="font-medium">{product.stockQuantity ?? 0}</span>
            </p>
          </div>

          {/* Add/Remove Toggle */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              type="button"
              onClick={() => setIsAddition(false)}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                !isAddition ? 'bg-red-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Minus className="w-4 h-4 inline mr-1" />
              Decrease
            </button>
            <button
              type="button"
              onClick={() => setIsAddition(true)}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                isAddition ? 'bg-green-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Plus className="w-4 h-4 inline mr-1" />
              Increase
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantity *
            </label>
            <input
              type="number"
              min="1"
              max={!isAddition ? product.stockQuantity : undefined}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Enter quantity"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason *
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
            >
              <option value={InventoryReason.ADJUSTMENT}>Correction / Count Adjustment</option>
              <option value={InventoryReason.CANCELLATION}>Damaged / Expired</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note (optional)
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., Damaged during storage"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
            />
          </div>

          {quantity && parseInt(quantity) > 0 && (
            <div className={`border rounded-lg p-3 ${
              newQuantity < 0 ? 'bg-red-50 border-red-200' : 
              isAddition ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'
            }`}>
              <p className={`text-sm ${
                newQuantity < 0 ? 'text-red-700' : 
                isAddition ? 'text-green-700' : 'text-orange-700'
              }`}>
                {newQuantity < 0 
                  ? 'Cannot reduce below zero!'
                  : <>New stock level will be: <span className="font-bold">{newQuantity}</span></>
                }
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-[#548B2E] text-white rounded-lg hover:bg-[#3f6b20] transition-colors disabled:opacity-50"
              disabled={loading || !quantity || parseInt(quantity) <= 0 || newQuantity < 0}
            >
              {loading ? 'Processing...' : 'Apply Adjustment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InventoryManagement;